import { useEffect, useState } from "react";
import Confetti from "react-confetti";

import { COLORS } from "@/constants/colors";

type ConfettiEffectProps = {
    isActive: boolean;
    numberOfPieces?: number;
    duration?: number;
};

export const ConfettiEffect = ({ isActive, numberOfPieces = 500, duration = 1000 }: ConfettiEffectProps) => {
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== "undefined" ? window.innerWidth : 800,
        height: typeof window !== "undefined" ? window.innerHeight : 600,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        if (typeof window !== "undefined") {
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }
    }, []);

    if (!isActive) {
        return null;
    }

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 9999,
                pointerEvents: "none",
            }}
        >
            <Confetti
                key={`corner-${isActive ? "active" : "inactive"}`}
                width={windowSize.width}
                height={windowSize.height}
                numberOfPieces={numberOfPieces}
                recycle={false}
                tweenDuration={duration}
                gravity={0.5}
                friction={0.99}
                wind={0}
                confettiSource={{
                    x: Math.max(windowSize.width - 20, 0),
                    y: Math.max(windowSize.height - 20, 0),
                    w: 20,
                    h: 20,
                }}
                initialVelocityX={{ min: -10, max: -20 }}
                initialVelocityY={{ min: -10, max: -40 }}
                drawShape={(ctx) => {
                    // Draw a thin strip to better visualize rotation
                    ctx.beginPath();
                    ctx.rect(-2, -8, 4, 16);
                    ctx.fill();
                }}
                colors={COLORS}
            />
        </div>
    );
};

export const useConfetti = () => {
    const [isActive, setIsActive] = useState(false);

    const triggerConfetti = () => {
        setIsActive(false);
        setTimeout(() => {
            setIsActive(true);
        }, 10);
    };

    const stopConfetti = () => {
        setIsActive(false);
    };

    return {
        isActive,
        triggerConfetti,
        stopConfetti,
    };
};
